<script setup lang="tsx">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EditDeleteButtons from '../widgets/EditDeleteButtons.vue'
import CancelSaveButtons from '../widgets/CancelSaveButtons.vue'
import InstanceSelect from '../widgets/InstanceSelect.vue'
import sliderStore from '@/stores/slider'
import toastStore from '@/stores/toast'
import http from '@/services/http'
import type { BusinessProcess, CustomFieldDefinition, Response } from '@/models/api-generated'
import { BusinessProcessReference, PermAllBPDelete, PermOwnBPDelete } from '@/models/enums-generated'
import ResponsibleSelect from '../widgets/ResponsibleSelect.vue'
import { defaultInstance, genRouteID, hasPermissionOwn } from '@/services/utils'
import URLReference from '../comment_references/URLReference.vue'
import DocumentReference from '../comment_references/DocumentReference.vue'
import { type CustomValueMap, fetchCustomValues } from '@/services/custom'
import CustomValueEdit from '../custom_field/CustomValueEdit.vue'
import { viewing } from '@/services/utils'

const defaultBP: BusinessProcess = {
	id: 0,
	name: '',
	instance: defaultInstance(),
	comment: '',
	description: '',
	created_by: 0,
	security_need: '',
	security_c: 1,
	security_i: 1,
	security_a: 1,
	urlReferences: [],
	documentReferences: [],
	customFields: {},
}

const props = defineProps<{ bpId: number }>()
const { t } = useI18n()
const slider = sliderStore()
const toaster = toastStore()
const isAdd = computed(() => props.bpId === 0)
const isEdit = ref(false)
const businessProcess = ref<BusinessProcess>({ ...defaultBP })
const canDelete = computed(() => hasPermissionOwn(businessProcess.value.responsible, PermAllBPDelete, PermOwnBPDelete) && !isAdd.value)
const canEditInstance = computed(() => viewing.value === 0)
const isValidInstance = computed(() => viewing.value !== 0 || businessProcess.value.instance?.id > 0)
const isValidName = computed(() => businessProcess.value.name.trim().length > 0)
const canSave = computed(() => isValidName.value && isValidInstance.value)
const custom = ref<CustomFieldDefinition[]>([])
const customValues = ref<CustomValueMap>()

onMounted(async () => {
	await refresh()
	slider.watchChanges(businessProcess)
})

watch(() => props.bpId, refresh)
watch(isEdit, () => slider.setFooter(editBtns))

const editBtns = (
	<div class="d-flex gap-2">
		<EditDeleteButtons deleteId="delete-bp" canDelete={canDelete} onDelete={remove} />
		<CancelSaveButtons class="flex-fill" saveId="save-bp" cancelId="cancel-bp" canSave={canSave} onCancel={cancel} onSave={save} />
	</div>
)

async function refresh() {
	isEdit.value = false
	slider.setFooter(editBtns)
	if (!isAdd.value) {
		const { data } = await http.get<Response<BusinessProcess>>(`/api/bp/${props.bpId}`)
		if (data.data) businessProcess.value = data.data
	} else {
		businessProcess.value = defaultBP
		if (viewing.value > 0) {
			businessProcess.value.instance.id = viewing.value
		}
	}

	await getCustomFields()
}

async function cancel() {
	const doCancel = async () => {
		if (isAdd.value) {
			slider.close()
		} else {
			await refresh()
		}
	}

	if (slider.getHasUnsavedChanges()) {
		slider.requestCancel(doCancel)
	} else {
		await doCancel()
	}
}

function remove() {
	slider.requestDeleteConfirm(t('misc.confirmDelete'), 'bp', confirmDelete)
}

async function confirmDelete() {
	const { status } = await http.get(`/api/bp/delete/${genRouteID(businessProcess.value.id, '', 'BusinessProcess')}`)
	if (status === 200) {
		toaster.showSuccess(t('misc.successfullyDeleted'))
		slider.setUnsavedChanges(false)
		slider.close()
		window.location.reload()
		// currently using window reload, but will want to use the refreshcallback in the future
	}
}

async function save() {
	const data = businessProcess.value
	if (customValues.value) data.customFields = customValues.value

	const url = '/api/bp'

	const { status } = await http.post<Response<BusinessProcess>>(url, data)
	if (status === 200) {
		toaster.showSuccess(t(isAdd.value ? 'misc.successfullyAdded' : 'misc.successfullyEdited'))
		slider.setUnsavedChanges(false)
		slider.close()
		window.location.reload()
	}
}

async function getCustomFields() {
	const { data } = await http.get<Response<CustomFieldDefinition[]>>(`api/customFields/business_processes`)
	if (data.data?.length) custom.value.push(...data.data)

	if (!isAdd.value) customValues.value = await fetchCustomValues('business_processes', props.bpId)
}

function updateCustomValue(fieldId: number, value: string) {
	customValues.value ??= {}
	customValues.value[fieldId] ??= {}
	customValues.value[fieldId][props.bpId] = value
}
</script>

<template>
	<div>
		<div class="row">
			<label class="col-4 col-form-label">{{ t('misc.name') }}</label>
			<div class="col-8 d-flex flex-column">
				<div>
					<input
						data-test="bp-name"
						class="form-control mb-2 w-100"
						v-model="businessProcess.name"
						:class="{ 'is-invalid': !isValidName }"
						required
					/>
					<div class="invalid-feedback">{{ $t('misc.validName') }}</div>
				</div>
			</div>
		</div>

		<div class="row">
			<label class="col-4 col-form-label">{{ t('responsible') }}</label>
			<div class="col-8 d-flex justify-content-end">
				<ResponsibleSelect data-test="bp-owner" v-model="businessProcess.responsible" class="mb-2"></ResponsibleSelect>
			</div>
		</div>

		<div v-if="canEditInstance" class="row">
			<label class="col-4 col-form-label">{{ t('institution') }}</label>
			<div class="col-8 d-flex flex-column">
				<div>
					<InstanceSelect
						data-test="bp-instance"
						v-model="businessProcess.instance"
						:class="{ 'is-invalid': !isValidInstance }"
						class="mb-2 w-100"
					/>
					<div class="invalid-feedback">{{ $t('misc.validInstance') }}</div>
				</div>
			</div>
		</div>
		<div class="row">
			<label class="col-4 col-form-label">{{ t('misc.comment') }}</label>
			<div class="col-8">
				<textarea data-test="bp-comment" class="form-control" v-model="businessProcess.comment" maxlength="500"></textarea>
			</div>
		</div>

		<div class="row mt-2" v-for="cf in custom">
			<div class="col col-form-label">
				{{ cf.label }}
			</div>
			<div class="col">
				<CustomValueEdit
					:field="cf"
					:modelValue="customValues?.[cf.id]?.[props.bpId] ?? ''"
					@update:modelValue="(val) => updateCustomValue(cf.id, val ?? '')"
				/>
			</div>
		</div>

		<h5 class="mt-3">{{ t('securityNeed.label') }}</h5>

		<div class="row align-items-center mb-2">
			<label class="col-4 col-form-label">{{ t('confidentiality') }}</label>
			<div class="col-8">
				<div class="btn-group w-100" role="group">
					<input type="radio" class="btn-check" id="sn-conf1" :value="1" v-model="businessProcess.security_c" />
					<label class="btn btn-outline-success" for="sn-conf1">{{ t('normal') }}</label>

					<input type="radio" class="btn-check" id="sn-conf2" :value="2" v-model="businessProcess.security_c" />
					<label class="btn btn-outline-warning" for="sn-conf2">{{ t('big') }}</label>

					<input type="radio" class="btn-check" id="sn-conf3" :value="3" v-model="businessProcess.security_c" />
					<label class="btn btn-outline-danger" for="sn-conf3">{{ t('veryBig') }}</label>
				</div>
			</div>
		</div>

		<div class="row align-items-center mb-2">
			<label class="col-4 col-form-label">{{ t('integrity') }}</label>
			<div class="col-8">
				<div class="btn-group w-100" role="group">
					<input type="radio" class="btn-check" id="sn-integ1" :value="1" v-model="businessProcess.security_i" />
					<label class="btn btn-outline-success" for="sn-integ1">{{ t('normal') }}</label>

					<input type="radio" class="btn-check" id="sn-integ2" :value="2" v-model="businessProcess.security_i" />
					<label class="btn btn-outline-warning" for="sn-integ2">{{ t('big') }}</label>

					<input type="radio" class="btn-check" id="sn-integ3" :value="3" v-model="businessProcess.security_i" />
					<label class="btn btn-outline-danger" for="sn-integ3">{{ t('veryBig') }}</label>
				</div>
			</div>
		</div>

		<div class="row align-items-center mb-2">
			<label class="col-4 col-form-label">{{ t('availability') }}</label>
			<div class="col-8">
				<div class="btn-group w-100" role="group">
					<input type="radio" class="btn-check" id="sn-avail1" :value="1" v-model="businessProcess.security_a" />
					<label class="btn btn-outline-success" for="sn-avail1">{{ t('normal') }}</label>

					<input type="radio" class="btn-check" id="sn-avail2" :value="2" v-model="businessProcess.security_a" />
					<label class="btn btn-outline-warning" for="sn-avail2">{{ t('big') }}</label>

					<input type="radio" class="btn-check" id="sn-avail3" :value="3" v-model="businessProcess.security_a" />
					<label class="btn btn-outline-danger" for="sn-avail3">{{ t('veryBig') }}</label>
				</div>
			</div>
		</div>
	</div>
	<div>
		<div class="col">
			<h6>{{ $t('reference.label', 2) }}</h6>
		</div>
	</div>
	<hr class="mt-2 mb-2 border-1" />
	<div class="row">
		<div class="col-5">
			<URLReference
				v-model="businessProcess.urlReferences"
				:owner-type="BusinessProcessReference"
				:owner-id="businessProcess.id"
				:can-edit="true"
			/>
		</div>

		<div class="col-auto d-flex">
			<div class="vr mx-3"></div>
		</div>

		<div class="col-5">
			<DocumentReference
				v-model="businessProcess.documentReferences"
				:owner-type="BusinessProcessReference"
				:owner-id="businessProcess.id"
				:can-edit="true"
			/>
		</div>
	</div>
</template>

<style lang="sass" scoped>
.fixed-badge
  max-height: 1.5rem
</style>
